import React, { useState } from "react";
import { Calendar as BigCalendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import dayjs from "dayjs";

import useCalendarData from "./hooks/useCalendarData";
import CustomEvent from "./components/CustomEvent";
import CustomDateCellWrapper from "./components/CustomDateCellWrapper";
import Legend from "./components/Legend";
import AddEventModal from "./components/AddEventModal";
import EditEventModal from "./components/EditEventModal";

import { addEvent, editEvent, deleteEvent } from "../../utils/api";

// Setup for BigCalendar date localization
const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const Calendar = ({ defaultView = "month", workOrderId = null, violation_id = null, architectural_request_id = null }) => {
  const [currentView, setCurrentView] = useState(defaultView);
  const { events, setEvents, users } = useCalendarData(workOrderId, violation_id, architectural_request_id);

  const [newEventModalOpen, setNewEventModalOpen] = useState(false);
  const [editEventModalOpen, setEditEventModalOpen] = useState(false);

  const [newEvent, setNewEvent] = useState({
    title: "",
    start: null,
    end: null,
    assigned_to_user_id: "",
    description: "",
  });

  const [selectedEvent, setSelectedEvent] = useState(null);

  const eventPropGetter = (event) => {
    let backgroundColor = "#3174ad";
    if (event.violation_id) backgroundColor = "#d32f2f";
    else if (event.architectural_request_id) backgroundColor = "#FFD700";
    return { style: { backgroundColor } };
  };

  const handleAddEvent = async () => {
    const currentUserId = localStorage.getItem("userId") || sessionStorage.getItem("userId");
    if (!currentUserId) {
      alert("User not identified. Please login again.");
      return;
    }

    const { title, start, end, assigned_to_user_id } = newEvent;
    if (!title || !start || !end || !assigned_to_user_id) {
      alert("Please fill in all required fields.");
      return;
    }

    try {
      const response = await addEvent({
        work_order_id: workOrderId,
        violation_id,
        architectural_request_id,
        title,
        description: newEvent.description || "No description provided",
        start_time: start,
        end_time: end,
        created_by_user_id: currentUserId,
        assigned_to_user_id,
      });

      setEvents((prev) => [...prev, { ...newEvent, id: response.data.id, architectural_request_id }]);
      setNewEvent({ title: "", start: null, end: null, assigned_to_user_id: "", description: "" });
      setNewEventModalOpen(false);
      alert("Event added successfully!");
    } catch (error) {
      console.error("Add event failed:", error);
      alert("Failed to add event.");
    }
  };

  const handleEditEvent = async () => {
    if (!selectedEvent) return;
    try {
      await editEvent(selectedEvent.id, {
        title: selectedEvent.title,
        description: selectedEvent.description,
        start_time: selectedEvent.start,
        end_time: selectedEvent.end,
        assigned_to_user_id: selectedEvent.assigned_to_user_id,
      });

      setEvents((prev) =>
        prev.map((event) => (event.id === selectedEvent.id ? selectedEvent : event))
      );
      setEditEventModalOpen(false);
      alert("Event updated successfully!");
    } catch (error) {
      console.error("Edit event failed:", error);
      alert("Failed to update event.");
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    try {
      await deleteEvent(selectedEvent.id);
      setEvents((prev) => prev.filter((event) => event.id !== selectedEvent.id));
      setEditEventModalOpen(false);
      alert("Event deleted successfully!");
    } catch (error) {
      console.error("Delete event failed:", error);
      alert("Failed to delete event.");
    }
  };

  const handleSlotClick = (slotInfo) => {
    setNewEvent({ ...newEvent, start: slotInfo.start, end: slotInfo.end });
    setNewEventModalOpen(true);
  };

  const handleEventClick = (event) => {
    setSelectedEvent({
      id: event.id,
      title: event.title,
      description: event.description,
      assigned_to_user_id: event.assigned_to_user_id,
      start: event.rawStart, // ✅ use raw string from DB
      end: event.rawEnd,
    });
    setEditEventModalOpen(true);
  };
  

  return (
    <div style={{ height: "500px", margin: "20px" }}>
      <Legend />

      <BigCalendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 450 }}
        view={currentView}
        onView={setCurrentView}
        views={["month", "week", "day", "agenda"]}
        popup
        selectable
        onSelectSlot={handleSlotClick}
        onSelectEvent={handleEventClick}
        eventPropGetter={eventPropGetter}
        components={{
          event: (props) => <CustomEvent {...props} currentView={currentView} />,
          dateCellWrapper: (props) => (
            <CustomDateCellWrapper {...props} events={events} currentView={currentView} />
          ),
        }}
      />

      {/* Add Event Modal */}
      <AddEventModal
        open={newEventModalOpen}
        onClose={() => setNewEventModalOpen(false)}
        onSave={handleAddEvent}
        eventData={newEvent}
        setEventData={setNewEvent}
        users={users}
      />

      {/* Edit Event Modal */}
      <EditEventModal
        open={editEventModalOpen}
        onClose={() => setEditEventModalOpen(false)}
        onSave={handleEditEvent}
        onDelete={handleDeleteEvent}
        eventData={selectedEvent}
        setEventData={setSelectedEvent}
      />
    </div>
  );
};

export default Calendar;
